/**
 * Which locations you actually have to collect from, on a given day and between given hours.
 *
 * The Directory lists every location we hold, which answers "who are our customers" and nothing
 * else. The question planners ask it is a different one: *what is on the ground on Tuesday, and
 * what of that is open between five and six.* Answering that meant reading the slots table by eye
 * and cross-referencing postcodes, which is why it mostly got answered from memory.
 *
 * The answer lives in the slots, not the locations — a location is only "a Monday collection"
 * because one of its slots runs on Mondays — so everything here starts from the slot list and
 * comes back with locations. The same result feeds the list and the map, so a pin and a row can
 * never disagree about what is collectable.
 */
import { AvailabilitySlot, DayOfWeek, Location } from "@/types/collections"

export const DAY_NAMES: DayOfWeek[] = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
]

export const DAY_LABELS: Record<DayOfWeek, string> = {
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
  sunday: "Sunday",
}

/** For the day toggles, where seven full names would wrap the filter bar onto two rows. */
export const DAY_SHORT_LABELS: Record<DayOfWeek, string> = {
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
  sunday: "Sun",
}

const MINUTES_PER_DAY = 24 * 60

export interface CoverageFilter {
  /** Days to include. Empty means every day — an empty filter is "show me everything". */
  days: DayOfWeek[]
  /** Start of the window of interest, `HH:mm`. Null means "from the start of the day". */
  from: string | null
  /** End of the window of interest, `HH:mm`. Null means "until the end of the day". */
  to: string | null
}

export const NO_COVERAGE_FILTER: CoverageFilter = { days: [], from: null, to: null }

export function isCoverageFilterActive(filter: CoverageFilter): boolean {
  return filter.days.length > 0 || filter.from !== null || filter.to !== null
}

/**
 * `HH:mm` to minutes past midnight, or null if it isn't a time.
 *
 * Tolerates the trailing seconds Postgres `time` columns serialise with (`08:00:00`), because the
 * same string reaches this module from the database and from an `<input type="time">`, and those
 * two do not agree on the format.
 */
export function timeToMinutes(value: string | null | undefined): number | null {
  if (!value) return null
  const match = /^(\d{1,2}):(\d{2})/.exec(value.trim())
  if (!match) return null
  const hours = Number(match[1])
  const minutes = Number(match[2])
  if (hours > 23 || minutes > 59) return null
  return hours * 60 + minutes
}

/** A half-open `[start, end)` span of minutes past midnight. */
type MinuteRange = [number, number]

/**
 * The span(s) a window covers, given that collection windows cross midnight.
 *
 * A 22:00–02:00 slot is a real thing on the night injections, and read literally its end is
 * before its start, so a naive `start <= t && t < end` test matches nothing at all and the stop
 * disappears from the board on exactly the shift that needs it. Anything ending before it starts
 * is therefore split in two: tonight's tail, and tomorrow's head.
 *
 * A window whose start and end are equal is a data error rather than a 24-hour slot. It is
 * treated as the single minute at its start, so it can still match a window covering that minute
 * and the location does not silently vanish while someone fixes the record.
 */
function rangesFor(start: number, end: number): MinuteRange[] {
  if (end > start) return [[start, end]]
  if (end < start) {
    return [
      [start, MINUTES_PER_DAY],
      [0, end],
    ]
  }
  return [[start, start + 1]]
}

/**
 * Overlap, not containment.
 *
 * A slot open 04:00–07:00 is a correct answer to "what can I collect between 05:00 and 06:00",
 * even though it neither sits inside that hour nor starts in it. The question the filter is
 * standing in for is "if I have a van out at this time, where can it go", and the answer is
 * every window that is open for any part of it.
 *
 * Touching ends do not count: a slot closing at 05:00 is shut for the whole of an 05:00–06:00
 * window, so the comparison is strict on both sides.
 */
function rangesOverlap(a: MinuteRange[], b: MinuteRange[]): boolean {
  return a.some(([aStart, aEnd]) => b.some(([bStart, bEnd]) => aStart < bEnd && bStart < aEnd))
}

/** The window a filter describes, or null when it places no constraint on time. */
function filterRanges(filter: CoverageFilter): MinuteRange[] | null {
  const from = timeToMinutes(filter.from)
  const to = timeToMinutes(filter.to)
  if (from === null && to === null) return null
  return rangesFor(from ?? 0, to ?? MINUTES_PER_DAY)
}

/**
 * Does this slot answer the filter?
 *
 * Inactive slots never do. They are the record of an arrangement that has ended, and putting a
 * pin on the map for one sends a van to a site that is not expecting it.
 */
export function slotMatchesFilter(slot: AvailabilitySlot, filter: CoverageFilter): boolean {
  if (!slot.is_active) return false

  if (filter.days.length > 0) {
    const days = slot.days ?? []
    if (!filter.days.some((day) => days.includes(day))) return false
  }

  const window = filterRanges(filter)
  if (!window) return true

  const start = timeToMinutes(slot.start_time)
  const end = timeToMinutes(slot.end_time)
  // An unreadable time is included rather than dropped. Over-including puts a pin on the map that
  // a planner can dismiss; under-including hides a collection nobody knows is missing.
  if (start === null || end === null) return true

  return rangesOverlap(rangesFor(start, end), window)
}

export interface CoveredLocation {
  location: Location
  /** The slots that put this location in the answer, earliest first. */
  slots: AvailabilitySlot[]
}

/**
 * The locations to show, each with the slots that earned it a place.
 *
 * With no filter on, that is every location — including the ones holding no slots at all, which
 * are usually sites mid-onboarding and are exactly what someone opening the map wants to see.
 * With a filter on, a location has to have a matching slot to appear: "show me Tuesday" that
 * still shows the sites we never visit on a Tuesday has not answered anything.
 */
export function coverageFor(
  locations: Location[],
  slots: AvailabilitySlot[],
  filter: CoverageFilter,
): CoveredLocation[] {
  const active = isCoverageFilterActive(filter)

  const byLocation = new globalThis.Map<string, AvailabilitySlot[]>()
  for (const slot of slots) {
    if (!slotMatchesFilter(slot, filter)) continue
    const existing = byLocation.get(slot.location_id)
    if (existing) existing.push(slot)
    else byLocation.set(slot.location_id, [slot])
  }

  const covered: CoveredLocation[] = []
  for (const location of locations) {
    const matching = byLocation.get(location.id) ?? []
    if (active && matching.length === 0) continue
    covered.push({
      location,
      slots: [...matching].sort(
        (a, b) => (timeToMinutes(a.start_time) ?? 0) - (timeToMinutes(b.start_time) ?? 0),
      ),
    })
  }
  return covered
}

/**
 * The subset that can be drawn.
 *
 * Null Island is excluded alongside the missing coordinates it is a symptom of: a location saved
 * before Samsara returned its geocode lands at 0,0, and one bad record there drags the map's
 * bounds from London to the Gulf of Guinea and zooms every real pin into the same speck.
 */
export function pinnableLocations(covered: CoveredLocation[]): CoveredLocation[] {
  return covered.filter(({ location }) => {
    const { latitude: lat, longitude: lng } = location
    if (lat == null || lng == null) return false
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false
    return lat !== 0 || lng !== 0
  })
}

/** "Mondays and Tuesdays, 05:00–06:00" — what the filter bar is currently claiming. */
export function describeCoverageFilter(filter: CoverageFilter): string {
  const parts: string[] = []

  if (filter.days.length > 0) {
    const ordered = DAY_NAMES.filter((day) => filter.days.includes(day))
    const names = ordered.map((day) => `${DAY_LABELS[day]}s`)
    parts.push(
      names.length === 1 ? names[0] : `${names.slice(0, -1).join(", ")} and ${names.at(-1)}`,
    )
  }

  if (filter.from !== null || filter.to !== null) {
    if (filter.from !== null && filter.to !== null) parts.push(`${filter.from}–${filter.to}`)
    else if (filter.from !== null) parts.push(`from ${filter.from}`)
    else parts.push(`until ${filter.to}`)
  }

  return parts.join(", ")
}
